import React from 'react';
import { motion } from 'framer-motion';
import { 
  GraduationCap, 
  Calendar, 
  DollarSign, 
  MapPin,
  Bookmark,
  BookmarkCheck,
  Building,
  Book,
  Award
} from 'lucide-react';
import Button from '../ui/Button';
import type { Scholarship } from '../../lib/types';

interface ScholarshipCardProps {
  scholarship: Scholarship;
  isBookmarked: boolean;
  onToggleBookmark: () => void;
  className?: string;
}

const ScholarshipCard: React.FC<ScholarshipCardProps> = ({
  scholarship,
  isBookmarked,
  onToggleBookmark,
  className = ''
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.2 }}
      className={`bg-white rounded-[2rem] shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden h-full ${className}`}
    >
      <div className="p-8 flex flex-col h-full">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {scholarship.name}
            </h3>
            <div className="flex items-center text-gray-500">
              <Building className="h-4 w-4 mr-2" />
              <span className="font-medium">{scholarship.provider}</span>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleBookmark}
            icon={isBookmarked ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
            className="rounded-full"
          >
            {isBookmarked ? 'Saved' : 'Save'}
          </Button>
        </div>

        {/* Content */}
        <div className="flex-grow">
          {/* Description */}
          <div className="mb-8">
            <p className="text-gray-600 leading-relaxed">
              {scholarship.description}
            </p>
          </div>

          {/* Key Details */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-full mr-3">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="font-bold text-gray-900">
                    ${scholarship.amount?.toLocaleString() || 'Variable'}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-full mr-3">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Deadline</p>
                  <p className="font-bold text-gray-900">
                    {scholarship.deadline ? new Date(scholarship.deadline).toLocaleDateString() : 'Rolling'}
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-full mr-3">
                  <MapPin className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Available in</p>
                  <p className="font-bold text-gray-900">
                    {scholarship.countries.join(', ')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Academic Requirements Section */}
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <div className="flex items-center mb-4">
              <Award className="h-6 w-6 text-primary-600 mr-2" />
              <h4 className="text-lg font-bold text-gray-900">Academic Requirements</h4>
            </div>
            
            {/* Degree Types */}
            <div className="mb-4">
              <div className="flex items-center mb-2">
                <GraduationCap className="h-5 w-5 text-primary-600 mr-2" />
                <h5 className="font-semibold text-gray-800">Eligible Degrees</h5>
              </div>
              <div className="flex flex-wrap gap-2 ml-7">
                {scholarship.degree_types.map((degree, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 bg-primary-100 text-primary-800 rounded-lg font-medium text-sm border border-primary-200"
                  >
                    {degree}
                  </span>
                ))}
              </div>
            </div>

            {/* Fields of Study */}
            <div>
              <div className="flex items-center mb-2">
                <Book className="h-5 w-5 text-primary-600 mr-2" />
                <h5 className="font-semibold text-gray-800">Fields of Study</h5>
              </div>
              <div className="flex flex-wrap gap-2 ml-7">
                {scholarship.fields_of_study.map((field, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 bg-white text-gray-800 rounded-lg font-medium text-sm border border-gray-200 shadow-sm"
                  >
                    {field}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Eligibility Criteria */}
          {scholarship.eligibility_criteria && Object.keys(scholarship.eligibility_criteria).length > 0 && (
            <div className="mb-8">
              <h4 className="text-lg font-bold text-gray-900 mb-3">Additional Requirements</h4>
              <ul className="space-y-2">
                {Object.entries(scholarship.eligibility_criteria).map(([key, value]) => (
                  <li key={key} className="flex items-center text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mr-3" />
                    <span className="capitalize font-medium">{key}:</span>
                    <span className="ml-2">{value}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Apply Button - Always at the bottom */}
        <div className="mt-auto">
          <Button
            fullWidth
            className="rounded-full text-lg py-3"
          >
            Apply Now
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ScholarshipCard;